import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { useVolunteerEnrollmentService } from "modules/Clinical/SubjectEnrollment/api/useVolunteerEnrollment";
import {
  useScanSampleCollectionReportService,
  useScanSampleSeperationReportService,
} from "../api/useSession";
import { getGenerateReportFormFields } from "../helper/accessioningconstant";
import { GenerateReportFormValues } from "../helper/session.types";
import { showToastMessage } from "common/service/CommonService";

interface GenerateReportModalProps {
  isOpen: boolean;
  toggle: () => void;
  activeMainTab: "sessions" | "sampleCollection" | "sampleSeparation" | "scanProgressHistory" | null;
  studyId: number | null;
  periodOptions: Array<{ label: string; value: string }>;
  selectedTimepointId?: number[] | null;
  selectedAliquotId?: number | null;
  selectedDosingStationNo?: number | null;
  searchText?: string;
}

const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
  isOpen,
  toggle,
  activeMainTab,
  studyId,
  periodOptions,
  selectedTimepointId,
  selectedAliquotId,
  selectedDosingStationNo,
  searchText,
}) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const { CollectionSubjectReportMutation } = useScanSampleCollectionReportService();
  const { SeperationSubjectReportMutation } = useScanSampleSeperationReportService();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<GenerateReportFormValues>({
    defaultValues: {
      period: "ALL",
      subjectno: "",
      reporttype: "SUMMARY",
    },
  });

  // Fetch volunteers from VolunteerEnrollment/SelectAll for the current study
  const { SelectAllQuery: volunteersQuery } = useVolunteerEnrollmentService({
    studyid: studyId ? Number(studyId) : null,
    enabled: isOpen && !!studyId,
  });

  const subjectOptions = useMemo(() => {
    const list = (volunteersQuery.data as any)?.data || [];
    const options = list
      .filter((row: any) => row.subjectno)
      .map((row: any) => ({
        label: String(row.subjectno),
        value: String(row.subjectno),
      }));
    return [{ label: "All Subjects", value: "ALL" }, ...options];
  }, [volunteersQuery.data]);

  useEffect(() => {
    if (isOpen) {
      reset({
        period: "ALL",
        subjectno: subjectOptions[0]?.value || "",
        reporttype: "SUMMARY",
      });
    }
  }, [isOpen, reset, subjectOptions]);

  const handleGenerateReport = async (values: GenerateReportFormValues) => {
    if (!studyId) {
      showToastMessage("error", "Please select a study first");
      return;
    }

    if (!values.subjectno) {
      showToastMessage("error", "Please select a subject first");
      return;
    }

    const periodid = values.period !== "ALL" ? Number(values.period) : null;
    const subjectno = values.subjectno !== "ALL" ? values.subjectno : null;
    const reporttype = activeMainTab === "sampleSeparation" ? undefined : values.reporttype;

    try {
      setIsGenerating(true);
      let response: any = null;

      if (activeMainTab === "sampleCollection") {
        response = await CollectionSubjectReportMutation.mutateAsync({
          studyid: Number(studyId),
          periodid,
          // timepointid: selectedTimepointId,
          // dosingstationno: selectedDosingStationNo,
          searchtext: searchText || "",
          subjectno,
          reporttype: reporttype || "SUMMARY",
        });
      } else if (activeMainTab === "sampleSeparation") {
        response = await SeperationSubjectReportMutation.mutateAsync({
          studyid: Number(studyId),
          periodid,
          // timepointid: selectedTimepointId,
          // aliquotid: selectedAliquotId,
          // dosingstationno: selectedDosingStationNo,
          searchtext: searchText || "",
          subjectno,
        });
      }

      if (response) {
        let blob: Blob;
        if (response instanceof Blob) {
          blob = response;
        } else if (response?.data instanceof Blob) {
          blob = response.data;
        } else {
          blob = new Blob([response], { type: "application/pdf" });
        }
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
        toggle();
      } else {
        showToastMessage("error", "Failed to generate report");
      }
    } catch (err: any) {
      console.error("Report generation error:", err);
      showToastMessage("error", typeof err === "string" ? err : "Error generating report");
    } finally {
      setIsGenerating(false);
    }
  };

  const isSampleSeparation = activeMainTab === "sampleSeparation";

  const formFields = useMemo(() => {
    return getGenerateReportFormFields(
      periodOptions,
      subjectOptions,
      isSampleSeparation,
      volunteersQuery.isFetching
    );
  }, [periodOptions, subjectOptions, isSampleSeparation, volunteersQuery.isFetching]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i className="ri-file-pdf-2-line text-danger fs-20" />
          <span className="fw-bold">
            {isSampleSeparation
              ? "Generate Sample Separation Report"
              : "Generate Sample Collection Report"}
          </span>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        <div className="mb-3">
          <p className="text-muted fs-12 mb-0">
            Select the period, subject, and report format criteria to generate your PDF report.
          </p>
        </div>

        <CommonForm
          fields={formFields}
          control={control}
          errors={errors}
          columns={1}
          setValue={setValue}
          getValues={getValues}
        />
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="secondary" outline onClick={toggle} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(handleGenerateReport)}
          disabled={isGenerating}
          className="d-flex align-items-center"
        >
          {isGenerating ? (
            <>
              <Spinner size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            <>
              <i className="ri-file-download-line me-1" />
              Generate Report
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GenerateReportModal;
